import { useEffect, useMemo, useState } from "react";
import api from "../../../../services/api";
import { BackButton } from "../../../../components/button/back-button";
import { LoadingButton } from "../../../../components/button/loading-button";
import { useAuth } from "../../../../hooks/use-auth";
import { useToast } from "../../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../../utils";
import type { Tenant } from "../../../../types/tenant";

const weekDays = [
  { key: "mon", label: "Seg" },
  { key: "tue", label: "Ter" },
  { key: "wed", label: "Qua" },
  { key: "thu", label: "Qui" },
  { key: "fri", label: "Sex" },
  { key: "sat", label: "Sáb" },
];

export default function WeeklyScheduleConfig() {
  const { user } = useAuth();
  const { error, success } = useToast();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [unavailabilityStart, setUnavailabilityStart] = useState("");
  const [unavailabilityEnd, setUnavailabilityEnd] = useState("");

  useEffect(() => {
    if (!user?.appName) return;

    const fetchTenant = async () => {
      setIsLoading(true);
      try {
        const response = await api.get<Tenant>(`/tenant/${user.appName}`);
        setTenant({
          ...response.data,
          schedulingTime:
            response.data.schedulingTime === 30 ||
            response.data.schedulingTime === 60
              ? response.data.schedulingTime
              : 60,
          schedulingStartTime: response.data.schedulingStartTime ?? "07:00",
          schedulingEndTime: response.data.schedulingEndTime ?? "18:00",
          schedulingAllowSaturday: !!response.data.schedulingAllowSaturday,
          schedulingSaturdayStartTime:
            response.data.schedulingSaturdayStartTime ??
            response.data.schedulingStartTime ??
            "07:00",
          schedulingSaturdayEndTime:
            response.data.schedulingSaturdayEndTime ??
            response.data.schedulingEndTime ??
            "18:00",
          schedulingWeeklyAvailability:
            response.data.schedulingWeeklyAvailability ?? {},
          schedulingUnavailabilities:
            response.data.schedulingUnavailabilities ?? [],
        });
      } catch (err) {
        error(getApiErrorMessage(err));
        setTenant(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTenant();
  }, [user?.appName, error]);

  const timeSlots = useMemo(() => {
    const start = tenant?.schedulingStartTime;
    const end = tenant?.schedulingEndTime;
    const interval =
      tenant?.schedulingTime === 30 || tenant?.schedulingTime === 60
        ? tenant?.schedulingTime
        : 60;

    if (!start || !end) return [];

    const parseTime = (value: string) => {
      const [hour, minute] = value.split(":").map(Number);
      if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
      return hour * 60 + minute;
    };

    const startMinutes = parseTime(start);
    const parsedEndMinutes = parseTime(end);
    const endMinutes = parsedEndMinutes === 0 ? 24 * 60 : parsedEndMinutes;

    if (
      startMinutes === null ||
      endMinutes === null ||
      endMinutes <= startMinutes
    ) {
      return [];
    }

    const slots: string[] = [];
    for (
      let minutes = startMinutes;
      minutes < endMinutes;
      minutes += interval
    ) {
      const hour = String(Math.floor(minutes / 60)).padStart(2, "0");
      const minute = String(minutes % 60).padStart(2, "0");
      slots.push(`${hour}:${minute}`);
    }
    return slots;
  }, [
    tenant?.schedulingStartTime,
    tenant?.schedulingEndTime,
    tenant?.schedulingTime,
  ]);

  const normalizeWeeklyAvailability = (
    availability: Record<string, string[]>,
    slots: string[],
    allowSaturday: boolean,
  ) => {
    const slotSet = new Set(slots);
    const normalized: Record<string, string[]> = {};

    Object.entries(availability).forEach(([day, times]) => {
      const filtered = (times ?? []).filter((time) => slotSet.has(time));
      normalized[day] = Array.from(new Set(filtered)).sort();
    });

    if (!allowSaturday) {
      normalized.sat = [];
    }

    return normalized;
  };

  useEffect(() => {
    if (!tenant?.enabledScheduling) return;
    if (!tenant.schedulingWeeklyAvailability) return;
    if (!timeSlots.length) return;

    setTenant((prev) => {
      if (!prev) return prev;
      const current = prev.schedulingWeeklyAvailability;
      if (!current || Object.keys(current).length === 0) return prev;
      const normalized = normalizeWeeklyAvailability(
        current,
        timeSlots,
        !!prev.schedulingAllowSaturday,
      );
      if (JSON.stringify(normalized) === JSON.stringify(current)) {
        return prev;
      }
      return { ...prev, schedulingWeeklyAvailability: normalized };
    });
  }, [
    tenant?.enabledScheduling,
    tenant?.schedulingAllowSaturday,
    tenant?.schedulingWeeklyAvailability,
    timeSlots,
  ]);

  const hasWeeklyConfig = useMemo(() => {
    const availability = tenant?.schedulingWeeklyAvailability;
    if (!availability) return false;
    return Object.keys(availability).length > 0;
  }, [tenant?.schedulingWeeklyAvailability]);

  const buildDefaultAvailability = (allowSaturday: boolean) => {
    const availability: Record<string, string[]> = {};
    weekDays.forEach((day) => {
      if (day.key === "sat" && !allowSaturday) return;
      availability[day.key] = [...timeSlots];
    });
    return availability;
  };

  const toggleSlot = (dayKey: string, time: string) => {
    if (!tenant) return;
    if (dayKey === "sat" && !tenant.schedulingAllowSaturday) return;

    setTenant((prev) => {
      if (!prev) return prev;
      const currentAvailability = prev.schedulingWeeklyAvailability ?? {};
      const baseAvailability =
        Object.keys(currentAvailability).length === 0
          ? buildDefaultAvailability(!!prev.schedulingAllowSaturday)
          : { ...currentAvailability };
      const dayTimes = new Set(baseAvailability[dayKey] ?? []);

      if (dayTimes.has(time)) {
        dayTimes.delete(time);
      } else {
        dayTimes.add(time);
      }

      return {
        ...prev,
        schedulingWeeklyAvailability: {
          ...baseAvailability,
          [dayKey]: Array.from(dayTimes).sort(),
        },
      };
    });
  };

  const isSlotSelected = (dayKey: string, time: string) => {
    if (!tenant) return false;
    if (dayKey === "sat" && !tenant.schedulingAllowSaturday) return false;
    if (!hasWeeklyConfig) return true;
    return (
      tenant.schedulingWeeklyAvailability?.[dayKey]?.includes(time) ?? false
    );
  };

  const updateSchedulingField = (field: "schedulingTime", value: number) => {
    setTenant((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const handleSave = async () => {
    if (!tenant?.appName || isSaving) return;

    try {
      setIsSaving(true);
      await api.patch(`/tenant/weekly-schedule`, {
        schedulingWeeklyAvailability: tenant.schedulingWeeklyAvailability ?? {},
        schedulingTime: tenant.schedulingTime,
        schedulingUnavailabilities: tenant.schedulingUnavailabilities ?? [],
      });
      success("Agenda semanal atualizada com sucesso!");
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const addUnavailability = () => {
    const startAt = new Date(unavailabilityStart);
    const endAt = new Date(unavailabilityEnd);

    if (
      !unavailabilityStart ||
      !unavailabilityEnd ||
      Number.isNaN(startAt.getTime()) ||
      Number.isNaN(endAt.getTime()) ||
      startAt >= endAt
    ) {
      error("Informe um período válido, com o fim posterior ao início.");
      return;
    }

    setTenant((current) =>
      current
        ? {
            ...current,
            schedulingUnavailabilities: [
              ...(current.schedulingUnavailabilities ?? []),
              { startAt: startAt.toISOString(), endAt: endAt.toISOString() },
            ].sort(
              (a, b) =>
                new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
            ),
          }
        : current,
    );
    setUnavailabilityStart("");
    setUnavailabilityEnd("");
  };

  const removeUnavailability = (index: number) => {
    setTenant((current) =>
      current
        ? {
            ...current,
            schedulingUnavailabilities: (
              current.schedulingUnavailabilities ?? []
            ).filter((_, itemIndex) => itemIndex !== index),
          }
        : current,
    );
  };

  const formatUnavailability = (value: string) =>
    new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));

  const schedulingEnabled = !!tenant?.enabledScheduling;

  return (
    <div className="app-page">
      <BackButton />
      <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="app-page-title">Agenda semanal</h1>
          <p className="text-sm text-slate-500">
            Defina os horários em que a equipe pode atender consultas.
          </p>
        </div>
        {schedulingEnabled && (
          <LoadingButton
            type="button"
            onClick={handleSave}
            isLoading={isSaving}
            loadingLabel="Salvando"
            className="btn btn-neutral"
          >
            Salvar alterações
          </LoadingButton>
        )}
      </div>

      {isLoading && (
        <p className="text-sm text-slate-600">Carregando configurações...</p>
      )}

      {!isLoading && !tenant && (
        <div className="app-empty-state">
          Não foi possível carregar as configurações do tenant.
        </div>
      )}

      {!isLoading && tenant && !schedulingEnabled && (
        <div className="app-empty-state">
          O agendamento de consultas está desabilitado para este tenant.
        </div>
      )}

      {!isLoading && tenant && schedulingEnabled && (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="app-card space-y-2 p-4">
              <label className="form-label" htmlFor="scheduling-time">
                Tempo das consultas (min)
              </label>
              <select
                id="scheduling-time"
                className="form-input"
                value={tenant.schedulingTime}
                onChange={(event) =>
                  updateSchedulingField(
                    "schedulingTime",
                    Number(event.target.value),
                  )
                }
              >
                <option value={30}>30</option>
                <option value={60}>60</option>
              </select>
            </div>
            <div className="app-card space-y-2 p-4">
              <p className="text-sm font-medium text-slate-700">Status</p>
              <p className="text-sm text-slate-500">
                {hasWeeklyConfig
                  ? "Horários personalizados configurados."
                  : "Todos os horários do intervalo estão ativos."}
              </p>
              <p className="text-xs text-slate-400">
                Clique nos horários para alternar a disponibilidade.
              </p>
            </div>
          </div>

          <div className="app-card space-y-4 p-4">
            <div>
              <p className="text-sm font-medium text-slate-700">
                Indisponibilidades
              </p>
              <p className="text-xs text-slate-500">
                Bloqueie períodos específicos para agendamentos da IA e da
                equipe.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
              <div>
                <label className="form-label" htmlFor="unavailability-start">
                  Início
                </label>
                <input
                  id="unavailability-start"
                  type="datetime-local"
                  className="form-input"
                  value={unavailabilityStart}
                  onChange={(event) =>
                    setUnavailabilityStart(event.target.value)
                  }
                />
              </div>
              <div>
                <label className="form-label" htmlFor="unavailability-end">
                  Fim
                </label>
                <input
                  id="unavailability-end"
                  type="datetime-local"
                  className="form-input"
                  value={unavailabilityEnd}
                  onChange={(event) => setUnavailabilityEnd(event.target.value)}
                />
              </div>
              <button
                type="button"
                className="btn btn-neutral"
                onClick={addUnavailability}
              >
                Adicionar período
              </button>
            </div>

            {(tenant.schedulingUnavailabilities ?? []).length === 0 ? (
              <p className="text-sm text-slate-500">
                Nenhuma indisponibilidade cadastrada.
              </p>
            ) : (
              <ul className="divide-y divide-slate-200">
                {(tenant.schedulingUnavailabilities ?? []).map(
                  (item, index) => (
                    <li
                      key={`${item.startAt}-${item.endAt}-${index}`}
                      className="flex items-center justify-between gap-4 py-3"
                    >
                      <span className="text-sm text-slate-700">
                        {formatUnavailability(item.startAt)} até{" "}
                        {formatUnavailability(item.endAt)}
                      </span>
                      <button
                        type="button"
                        className="text-sm font-medium text-red-600 hover:text-red-700"
                        aria-label={`Remover indisponibilidade de ${formatUnavailability(item.startAt)}`}
                        onClick={() => removeUnavailability(index)}
                      >
                        Remover
                      </button>
                    </li>
                  ),
                )}
              </ul>
            )}
          </div>

          {timeSlots.length === 0 ? (
            <div className="app-empty-state text-center">
              Defina o início e o fim do atendimento para carregar os horários.
            </div>
          ) : (
            <div className="app-card overflow-auto">
              <div className="min-w-[720px]">
                <div className="grid grid-cols-[90px_repeat(6,1fr)] bg-slate-50 text-xs font-semibold text-slate-500">
                  <div className="p-3">Hora</div>
                  {weekDays.map((day) => (
                    <div
                      key={day.key}
                      className="p-3 text-center uppercase tracking-wide"
                    >
                      {day.label}
                    </div>
                  ))}
                </div>

                {timeSlots.map((time) => (
                  <div
                    key={time}
                    className="grid grid-cols-[90px_repeat(6,1fr)] border-t border-slate-100"
                  >
                    <div className="p-3 text-xs font-semibold text-slate-600">
                      {time}
                    </div>
                    {weekDays.map((day) => {
                      const disabled =
                        day.key === "sat" && !tenant.schedulingAllowSaturday;
                      const selected = isSlotSelected(day.key, time);

                      return (
                        <button
                          key={`${day.key}-${time}`}
                          type="button"
                          onClick={() => toggleSlot(day.key, time)}
                          disabled={disabled}
                          aria-pressed={selected}
                          className={`h-10 border-l border-slate-100 transition ${
                            disabled
                              ? "bg-slate-100 cursor-not-allowed"
                              : selected
                                ? "bg-slate-900"
                                : "bg-white hover:bg-slate-50"
                          }`}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
